<template>
  <div class="custom-operations" data-testid="custom-operations">
    <CustomOperationsHeader
      class="custom-operations__filters"
      :updated-at="updatedAtMessage"
      :is-loading="isLoading && !updatedAtMessage"
    />
    <template v-if="hasError">
      <HelperViewStatus
        class="custom-operations__error"
        title="Não foi possível carregar os seus ativos"
        description="Aguarde um momento e tente novamente"
        button-label="Tentar novamente"
        status="alert"
        @click="getCustomOperationsData"
      />
    </template>
    <template v-else>
      <CustomOperationsTable
        :is-loading="isLoading"
        :items-per-page="pagination.itemsPerPage"
        :items="items"
      />
      <Pagination
        class="custom-operations__pagination"
        data-testid="custom-operations-pagination"
        :is-loading="isLoading"
        :total-pages="pagination.totalPages"
        :current-page="pagination.currentPage"
        @page-changed="handleOnPageChange"
      />
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';

import { CustomOperationsHeader } from './components/CustomOperationsHeader';
import { CustomOperationsTable } from './components/CustomOperationsTable';
import { HelperViewStatus } from '@warrenbrasil/nebraska-web';
import { Pagination } from '@/modules/common/components/Pagination';

import { isRequestError } from '@warrenbrasil/web-http';
import {
  getCustomOperationsList,
  CustomOperationPosition,
  GetCustomOperationsListResponse
} from '@/modules/trade/services/instruments';

const items = ref<CustomOperationPosition[]>([]);
const isLoading = ref<boolean>(false);
const hasError = ref<boolean>(false);
const updatedAtMessage = ref<string>('');
const abortSignal = ref<AbortController | null>(null);
const pagination = ref({
  currentPage: 1,
  totalPages: 1,
  itemsPerPage: 10
});

const mountView = (data: GetCustomOperationsListResponse) => {
  const {
    updatedAt,
    items: derivativesItems,
    pagination: { itemsPerPage, totalPages, page }
  } = data;
  updatedAtMessage.value = updatedAt.formatted;
  items.value = derivativesItems;
  pagination.value = {
    totalPages,
    itemsPerPage,
    currentPage: page
  };
};

const getCustomOperationsData = async () => {
  try {
    isLoading.value = true;
    hasError.value = false;
    if (abortSignal.value) abortSignal.value.abort();
    abortSignal.value = new AbortController();

    const data = await getCustomOperationsList(
      {
        itemsPerPage: pagination.value.itemsPerPage,
        page: pagination.value.currentPage
      },
      abortSignal.value.signal
    );

    mountView(data);
  } catch (e) {
    if (isRequestError(e) && e.type === 'canceled') return;
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

const handleOnPageChange = (page: number) => {
  pagination.value.currentPage = page;
  getCustomOperationsData();
};

onMounted(() => getCustomOperationsData());
</script>
<style lang="less" scoped>
.custom-operations {
  padding: @size-spacing-x600 @size-spacing-x500 0 @size-spacing-x500;

  .to-lg({ padding: @size-spacing-x400 @size-spacing-x400;});

  &__filters {
    margin-bottom: @size-spacing-x500;
  }

  &__pagination {
    margin: @size-spacing-x500 0;
  }

  &__error {
    margin: @size-spacing-x1800 auto @size-spacing-x400 auto;
  }
}
</style>
